import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography, Divider } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import { FormattedMessage, useModulesManager, useTranslations, baseApiUrl } from "@openimis/fe-core";

const styles = (theme) => ({
  item: theme.paper.item,
  jsonBlock: {
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(1.5),
    borderRadius: 4,
    maxHeight: 420,
    overflow: "auto",
    margin: 0,
  },
  sectionTitle: {
    fontWeight: 700,
    marginTop: theme.spacing(1.5),
  },
  fieldRow: {
    display: "flex",
    flexWrap: "wrap",
    padding: theme.spacing(0.5, 0),
    borderBottom: "1px solid #eee",
  },
  fieldLabel: {
    fontWeight: 600,
    minWidth: 260,
    marginRight: theme.spacing(1),
  },
  fieldValue: {
    wordBreak: "break-word",
    flex: 1,
  },
  rawDetails: {
    marginTop: theme.spacing(1.5),
  },
});

// Keys produced by the tablet's dynamic clinical/eligibility rule builder.
// Everything under jsonExt.eligibility.form_data (and each addon's
// jsonExt.eligibility.used_addons[].answers) is keyed by the field's raw
// "key"/id (e.g. "field_7") rather than its human-readable label, and — for
// choice fields — the value is already the display text the admin typed as
// a choice (there is no separate code list), so only the KEY needs
// resolving against the rule's field definitions.
const ELIGIBILITY_KEY = "eligibility";

const humanizeKey = (key) =>
  String(key || "")
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || String(key || "");

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (!value.length) return "-";
    return value.map((v) => (v && typeof v === "object" ? JSON.stringify(v) : String(v))).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

// Builds a fieldKey -> label map from a list of serialized eligibility (or
// addon) rules, each exposing `ui_schema.fields: [{ key, label, ... }]`.
// Never throws — a rule/field with an unexpected shape is skipped so one
// malformed rule can't break resolution for every other field.
const buildFieldLabelMap = (rules) => {
  const map = {};
  (Array.isArray(rules) ? rules : []).forEach((rule) => {
    const fields = rule?.ui_schema?.fields;
    if (!Array.isArray(fields)) return;
    fields.forEach((field) => {
      if (field && field.key && !map[field.key]) {
        map[field.key] = field.label || field.key;
      }
    });
  });
  return map;
};

const buildAddonFieldMaps = (addonRules) => {
  const maps = {};
  (Array.isArray(addonRules) ? addonRules : []).forEach((rule) => {
    const code = rule?.addon_code;
    if (!code) return;
    const fields = Array.isArray(rule?.required_fields) ? rule.required_fields : [];
    const map = {};
    fields.forEach((field) => {
      if (field && field.key) map[field.key] = field.label || field.key;
    });
    maps[code] = map;
  });
  return maps;
};

const fetchJsonSafe = async (url) => {
  try {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    return await res.json();
  } catch (error) {
    // Network/CORS/parse failure — resolution simply falls back to raw
    // field ids below rather than breaking the claim details page.
    return null;
  }
};

const FieldRow = ({ classes, label, value }) => (
  <div className={classes.fieldRow}>
    <Typography className={classes.fieldLabel}>{label}</Typography>
    <Typography className={classes.fieldValue}>{formatFieldValue(value)}</Typography>
  </div>
);

// Renders the claim's json_ext — the tablet captures several fields
// (clinical/eligibility rule builder answers, add-on answers, discharge
// status, laboratory investigations, imaging, treatment, MCH package
// track, etc.) that the core claim schema/UI has no dedicated
// columns/inputs for. Field ids captured by the rule builder are resolved
// to their human-readable label here (falling back to the raw id if the
// rule definition can't be fetched); every entry present in the payload is
// still shown — nothing configured by the admin is filtered out. Read-only
// by design — never editable from this panel.
const ClaimJsonExtPanel = ({ classes, claim }) => {
  const modulesManager = useModulesManager();
  const { formatMessage } = useTranslations("claim", modulesManager);

  const jsonExt = claim?.jsonExt;
  const hasData = !!jsonExt && typeof jsonExt === "object" && Object.keys(jsonExt).length > 0;

  const eligibility =
    hasData && jsonExt[ELIGIBILITY_KEY] && typeof jsonExt[ELIGIBILITY_KEY] === "object"
      ? jsonExt[ELIGIBILITY_KEY]
      : null;
  const formData =
    eligibility && eligibility.form_data && typeof eligibility.form_data === "object" ? eligibility.form_data : null;
  const hasFormData = !!formData && Object.keys(formData).length > 0;
  const usedAddons = Array.isArray(eligibility?.used_addons) ? eligibility.used_addons : [];
  const benefitPlanId = eligibility?.benefit_plan_id;
  const addonCodesKey = usedAddons.map((addon) => addon?.code || "").join("|");

  const [fieldLabelMap, setFieldLabelMap] = useState({});
  const [addonFieldMaps, setAddonFieldMaps] = useState({});

  // Single batch call for the benefit plan's eligibility rules — not one
  // call per field.
  useEffect(() => {
    let cancelled = false;
    if (!benefitPlanId || !hasFormData) {
      setFieldLabelMap({});
      return undefined;
    }
    (async () => {
      const body = await fetchJsonSafe(
        `${baseApiUrl}/sync/eligibility-rules/?benefit_plan_id=${encodeURIComponent(benefitPlanId)}`,
      );
      if (cancelled) return;
      setFieldLabelMap(buildFieldLabelMap(body?.rules));
    })();
    return () => {
      cancelled = true;
    };
  }, [benefitPlanId, hasFormData]);

  // Single batch call for ALL add-on rules — not one call per addon/field.
  useEffect(() => {
    let cancelled = false;
    if (!addonCodesKey) {
      setAddonFieldMaps({});
      return undefined;
    }
    (async () => {
      const body = await fetchJsonSafe(`${baseApiUrl}/sync/addon-rules/`);
      if (cancelled) return;
      setAddonFieldMaps(buildAddonFieldMaps(body?.addon_rules));
    })();
    return () => {
      cancelled = true;
    };
  }, [addonCodesKey]);

  // Anything in jsonExt that isn't the rule-builder clinical data handled
  // above (e.g. discharge_status, laboratory investigations, imaging,
  // treatment notes, MCH package track, or the non-field-answer bits of
  // `eligibility` such as benefit_plan_code/eligible_service_ids) — shown
  // with a humanized label so nothing is ever dropped from this view.
  const otherEntries = useMemo(() => {
    if (!hasData) return [];
    const entries = Object.entries(jsonExt).filter(([key]) => key !== ELIGIBILITY_KEY);
    if (eligibility) {
      Object.entries(eligibility)
        .filter(([key]) => key !== "form_data" && key !== "used_addons")
        .forEach(([key, value]) => entries.push([key, value]));
    }
    return entries;
  }, [hasData, jsonExt, eligibility]);

  return (
    <Grid item xs={12} className={classes.item}>
      <Typography variant="subtitle1">
        <FormattedMessage module="claim" id="ClaimJsonExtPanel.header" />
      </Typography>
      <Divider />
      {!hasData && (
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage module="claim" id="ClaimJsonExtPanel.noData" />
        </Typography>
      )}
      {hasData && (
        <>
          {hasFormData && (
            <>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage module="claim" id="ClaimJsonExtPanel.clinicalData" />
              </Typography>
              {Object.entries(formData).map(([key, value]) => (
                <FieldRow key={key} classes={classes} label={fieldLabelMap[key] || key} value={value} />
              ))}
            </>
          )}

          {usedAddons.map((addon, index) => {
            const map = addonFieldMaps[addon?.code] || {};
            const answers = addon?.answers && typeof addon.answers === "object" ? addon.answers : {};
            return (
              <React.Fragment key={`${addon?.code || "addon"}-${index}`}>
                <Typography className={classes.sectionTitle}>
                  {addon?.name || addon?.code || formatMessage("ClaimJsonExtPanel.addon")}
                </Typography>
                {Object.keys(answers).length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    <FormattedMessage module="claim" id="ClaimJsonExtPanel.noAddonAnswers" />
                  </Typography>
                )}
                {Object.entries(answers).map(([key, value]) => (
                  <FieldRow key={key} classes={classes} label={map[key] || key} value={value} />
                ))}
              </React.Fragment>
            );
          })}

          {otherEntries.length > 0 && (
            <>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage module="claim" id="ClaimJsonExtPanel.otherDetails" />
              </Typography>
              {otherEntries.map(([key, value]) => (
                <FieldRow key={key} classes={classes} label={humanizeKey(key)} value={value} />
              ))}
            </>
          )}

          <details className={classes.rawDetails}>
            <summary>{formatMessage("ClaimJsonExtPanel.rawJson")}</summary>
            <pre className={classes.jsonBlock}>{JSON.stringify(jsonExt, null, 2)}</pre>
          </details>
        </>
      )}
    </Grid>
  );
};

export default withStyles(styles)(ClaimJsonExtPanel);
